import { z } from "zod";
import { metricCatalogue } from "../data/catalogue";
import type { CompositeWeightMap, GeographyLevel, MetricId } from "@/features/explorer/types";

/**
 * Search-param schema for the explorer route (`/`). Selection and any active
 * composite weights are encoded so a view can be restored from a shared link.
 * All fields are optional; empty ones fall back to UI defaults.
 *
 * Weights travel as a compact CSV string: `metricId:weight,metricId:weight`
 * (e.g. `employment-rate:40,housing-affordability-ratio:60`). CSV keeps URLs
 * short and stable compared to JSON-encoding in a single param.
 */
export const explorerSearchSchema = z.object({
  compare: z.string().optional(),
  metricId: z.string().optional(),
  geographyLevel: z.string().optional(),
  statistic: z.string().optional(),
  period: z.string().optional(),
  weights: z.string().optional(),
  pins: z.string().optional(),
});

export type ExplorerSearchParams = z.infer<typeof explorerSearchSchema>;

const validMetricIds = new Set<string>(metricCatalogue.map((metric) => metric.id));

function isMetricId(value: string): value is MetricId {
  return validMetricIds.has(value);
}

export function parseCompositeWeights(raw: string | undefined): CompositeWeightMap {
  const weights: CompositeWeightMap = {};
  if (!raw) return weights;
  for (const part of raw.split(",")) {
    const separatorIndex = part.indexOf(":");
    if (separatorIndex < 1) continue;
    const id = part.slice(0, separatorIndex);
    const weight = Number(part.slice(separatorIndex + 1));
    if (isMetricId(id) && Number.isFinite(weight) && weight > 0) {
      weights[id] = weight;
    }
  }
  return weights;
}

export function encodeCompositeWeights(weights: CompositeWeightMap): string | undefined {
  const entries = Object.entries(weights).filter(
    (entry): entry is [string, number] => typeof entry[1] === "number" && entry[1] > 0,
  );
  if (entries.length === 0) return undefined;
  return entries.map(([id, weight]) => `${id}:${weight}`).join(",");
}

/**
 * Pinned areas travel as pipe-separated, per-geography-level CSV groups:
 * `local-authority:E06000001,E08000003|region:E12000001`. GSS codes never
 * overlap across levels (LA/county/region), but pinning is kept independent per
 * boundary resolution so switching granularity shows the shortlist for that
 * level rather than a mixed bag. Within a group, insertion order is preserved
 * so a shared link restores the same pinned strip ordering.
 */
export type PinnedCodesByLevel = Partial<Record<GeographyLevel, string[]>>;

const GEOGRAPHY_LEVELS: readonly GeographyLevel[] = ["local-authority", "county", "region"];

export function parsePinnedCodes(raw: string | undefined): PinnedCodesByLevel {
  const result: PinnedCodesByLevel = {};
  if (!raw) return result;
  for (const group of raw.split("|")) {
    const separatorIndex = group.indexOf(":");
    if (separatorIndex < 1) continue;
    const level = group.slice(0, separatorIndex);
    if (level !== "local-authority" && level !== "county" && level !== "region") continue;
    const codes = group
      .slice(separatorIndex + 1)
      .split(",")
      .map((part) => part.trim())
      .filter(Boolean);
    if (codes.length > 0) result[level] = codes;
  }
  return result;
}

export function encodePinnedCodes(byLevel: PinnedCodesByLevel): string | undefined {
  const groups: string[] = [];
  for (const level of GEOGRAPHY_LEVELS) {
    const codes = byLevel[level];
    if (!codes || codes.length === 0) continue;
    groups.push(`${level}:${[...new Set(codes)].join(",")}`);
  }
  if (groups.length === 0) return undefined;
  return groups.join("|");
}
